import { Link } from "react-router-dom";
import { ArrowUpRight, BrainCircuit, Workflow, Code2 } from "lucide-react";
import Seo from "@/components/Seo";
import { MaskedLine, Reveal } from "@/components/Reveal";

const SERVICES = [
  {
    id: "ai-development",
    index: "01",
    title: "AI Development",
    icon: BrainCircuit,
    problem: "You need AI in the product, but demos don't survive auth, evals, and cost control.",
    outcome: "Shipped features users touch, with guardrails and a path to iterate.",
    deliverables: [
      "Feature design + model/tooling choice for your stack",
      "Production integration (APIs, auth, data boundaries)",
      "Eval / quality baseline and failure modes documented",
      "Runbook for prompts, versions, and cost knobs"
    ],
    fit: "You have a clear user job-to-be-done and data/access to support it — not \"add ChatGPT somewhere.\"",
    cta: "Discuss AI",
    contactQuery: "need=ai"
  },
  {
    id: "n8n-automation",
    index: "02", 
    title: "n8n Automation",
    icon: Workflow,
    problem: "Ops live in spreadsheets and Slack; humans are the integration layer.",
    outcome: "Reliable workflows that run — monitored and changeable.",
    deliverables: [
      "Workflow audit + target-state map",
      "Production n8n flows (self-hosted or cloud) with error paths",
      "Credentials / env hygiene and handover docs",
      "Optional alerts + lightweight admin UI if ops need it"
    ],
    fit: "Recurring multi-step processes with clear systems of record (CRM, billing, support, internal tools).",
    cta: "Discuss automation",
    contactQuery: "need=n8n"
  },
  {
    id: "react-fullstack",
    index: "03",
    title: "React Full-Stack", 
    icon: Code2,
    problem: "You need a real app with auth and deploy discipline, not a prototype graveyard.",
    outcome: "A maintainable React full-stack product live in your infra.",
    deliverables: [
      "UX-light but production UI (your brand or ours)",
      "API / DB / auth as needed for the scope",
      "CI-friendly repo, env sample, deploy notes",
      "Handoff session with your engineers (if any)"
    ],
    fit: "Scope is a coherent product slice with a deadline — MVP, v1 module, or replacement of a painful internal tool.",
    cta: "Discuss React",
    contactQuery: "need=react"
  }
];

const ServiceCard = ({ service, delay = 0 }) => {
  const Icon = service.icon;
  
  return (
    <Reveal delay={delay}>
      <div className="group rounded-2xl border border-border bg-card p-8 transition-all duration-300 hover:border-coral/20 hover:shadow-lg">
        
        {/* Header */}
        <div className="flex items-start gap-6">
          <span className="font-display text-4xl font-extrabold text-outline transition-colors duration-500 group-hover:text-coral group-hover:[-webkit-text-stroke:0px]">
            {service.index}
          </span>
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <Icon className="h-6 w-6 text-coral" />
              <h3 className="font-display text-2xl font-bold tracking-tight">
                {service.title}
              </h3>
            </div>
          </div>
        </div>

        {/* Problem */}
        <div className="mt-6">
          <h4 className="mb-3 font-mono text-xs uppercase tracking-[0.2em] text-coral">
            Problem
          </h4>
          <p className="text-base leading-relaxed text-muted-foreground">
            {service.problem}
          </p>
        </div>

        {/* Outcome */}
        <div className="mt-6">
          <h4 className="mb-3 font-mono text-xs uppercase tracking-[0.2em] text-coral">
            Outcome
          </h4>
          <p className="text-base leading-relaxed text-foreground font-medium">
            {service.outcome}
          </p>
        </div>

        {/* Deliverables */}
        <div className="mt-6">
          <h4 className="mb-3 font-mono text-xs uppercase tracking-[0.2em] text-coral">
            Deliverables
          </h4>
          <ul className="space-y-2">
            {service.deliverables.map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                <span className="mt-2 block h-1 w-1 shrink-0 rounded-full bg-coral"></span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Fit when */}
        <div className="mt-6">
          <h4 className="mb-3 font-mono text-xs uppercase tracking-[0.2em] text-coral">
            Fit when
          </h4>
          <p className="text-sm text-muted-foreground">
            {service.fit}
          </p>
        </div>

        {/* CTA */}
        <div className="mt-8">
          <Link
            to={`/contact?${service.contactQuery}`}
            className="group inline-flex items-center gap-2 rounded-full bg-coral px-6 py-3 font-mono text-xs uppercase tracking-[0.15em] text-white transition-all duration-300 hover:bg-navy dark:hover:bg-coral-bright"
          >
            {service.cta}
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>

      </div>
    </Reveal>
  );
};

export default function Services() {
  return (
    <>
      <Seo
        title="Services — codeRealm"
        siteName="codeRealm"
        description="AI Development, n8n Automation, and React Full-Stack engineering — three crafts, one studio, production outcomes for SaaS and ops-led teams."
      />
      
      <section className="mx-auto max-w-7xl px-5 pb-24 pt-20 sm:px-8 sm:pt-28">
        
        {/* Header */}
        <div className="mb-16">
          <MaskedLine>
            <span className="font-mono text-xs uppercase tracking-[0.25em] text-coral">Services</span>
          </MaskedLine>
          
          <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_auto] lg:items-end">
            <div>
              <h1 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-6xl lg:text-7xl">
                <MaskedLine delay={0.12}>What we</MaskedLine>
                <MaskedLine delay={0.24}>
                  <span className="text-outline">build</span>
                </MaskedLine>
              </h1>
              
              <Reveal delay={0.35} className="mt-6 max-w-2xl">
                <p className="text-xl leading-relaxed text-muted-foreground">
                  Three crafts. One studio. Production outcomes for SaaS and ops-led teams.
                </p>
              </Reveal>
            </div>
            
            <Reveal delay={0.45}>
              <Link
                to="/contact"
                className="group flex items-center gap-2 rounded-full bg-coral px-6 py-3 font-mono text-xs uppercase tracking-[0.15em] text-white transition-all duration-300 hover:bg-navy dark:hover:bg-coral-bright"
              >
                Start a project
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </Reveal>
          </div>
        </div>

        {/* Service cards */}
        <div className="grid gap-8 lg:gap-12">
          {SERVICES.map((service, i) => (
            <ServiceCard 
              key={service.id} 
              service={service} 
              delay={0.5 + (i * 0.1)}
            />
          ))}
        </div>

        {/* Mix & match note */}
        <Reveal delay={1.0} className="mt-16">
          <div className="rounded-2xl border border-border bg-card/60 p-8 text-center">
            <p className="text-base leading-relaxed text-muted-foreground">
              Most clients mix crafts. Say what you need on the contact form; we'll recommend fixed vs retainer.
            </p>
            <div className="mt-6">
              <Link
                to="/how-we-work"
                className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.18em] text-coral transition-colors duration-300 hover:text-coral/80"
              >
                How we work
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
          </div>
        </Reveal>

        {/* Final CTA */}
        <Reveal delay={1.1} className="mt-16">
          <div className="border-t border-border pt-16 text-center">
            <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
              Ready when you are
            </h2>
            <p className="mt-4 text-lg text-muted-foreground">
              We reply within 1 business day · boutique studio, India + remote
            </p>
            <div className="mt-8">
              <Link
                to="/contact"
                className="group inline-flex items-center gap-2 rounded-full bg-coral px-8 py-4 font-mono text-xs uppercase tracking-[0.15em] text-white transition-all duration-300 hover:bg-navy dark:hover:bg-coral-bright"
              >
                Start a project
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
          </div>
        </Reveal>

      </section>
    </>
  );
}